import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { StatCard } from '@/components/dashboard/StatCard';
import { Combo, comboApi, UpdateComboRequest, EComboStatus } from '@/services/combo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Package,
  Pencil,
  Trash2,
  Loader2,
  DollarSign,
  Clock,
  LayoutGrid,
  CircleCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

export default function ComboDetail() {
  const { comboId } = useParams<{ comboId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    data: comboResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['combo', comboId],
    queryFn: () => comboApi.getById(Number(comboId)),
    enabled: !!comboId,
  });

  const combo = comboResponse?.data;

  const updateMutation = useMutation({
    mutationFn: (data: UpdateComboRequest) => comboApi.update(Number(comboId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combo', comboId] });
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      setIsEditDialogOpen(false);
      toast.success('Combo actualizado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => comboApi.remove(Number(comboId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      toast.success('Combo eliminado exitosamente');
      navigate('/combos');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout title="Cargando..." subtitle="">
        <div className="card-elevated p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Cargando combo...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Error" subtitle="">
        <Alert variant="destructive">
          <AlertDescription>
            Error al cargar el combo. Por favor, intenta de nuevo.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="link" asChild>
            <Link to="/combos">Volver a Combos</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!combo) {
    return (
      <DashboardLayout title="No encontrado" subtitle="">
        <div className="card-elevated p-8 text-center">
          <p className="text-muted-foreground">El combo solicitado no existe.</p>
          <Button variant="link" asChild className="mt-2">
            <Link to="/combos">Volver a Combos</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const servicesCount = combo.comboServices?.length ?? 0;

  return (
    <DashboardLayout
      title=""
      subtitle=""
      customHeader={
        <div className="space-y-4">
          {/* Breadcrumb */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/combos">Combos</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{combo.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Package className="h-7 w-7 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">{combo.name}</h1>
                  <StatusBadge status={combo.status} />
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Creado el {new Date(combo.createdAt).toLocaleDateString('es-CO', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
                Editar
              </Button>
              <Button
                variant="outline"
                className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4" />
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-6 animate-fade-in">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Precio"
            value={`$${formatCurrency(combo.price)}`}
            icon={DollarSign}
          />
          <StatCard
            title="Duración"
            value={`${combo.duration} días`}
            icon={Clock}
          />
          <StatCard
            title="Servicios incluidos"
            value={servicesCount}
            icon={LayoutGrid}
          />
          <StatCard
            title="Estado"
            value={combo.status === EComboStatus.ACTIVE ? 'Activo' : 'Inactivo'}
            icon={CircleCheck}
          />
        </div>

        {/* Services Included */}
        <div className="card-elevated p-6">
          <h2 className="text-base font-semibold text-foreground mb-4">
            Servicios incluidos ({servicesCount})
          </h2>

          {servicesCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              Este combo no tiene servicios asociados.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {combo.comboServices!.map((cs) => (
                <div
                  key={cs.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20"
                >
                  {cs.service?.icon && (
                    <span
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                      style={{
                        backgroundColor: cs.service?.color
                          ? `${cs.service.color}20`
                          : 'hsl(var(--muted))',
                      }}
                    >
                      {cs.service.icon}
                    </span>
                  )}
                  <div>
                    <p className="font-medium text-sm text-foreground">{cs.service?.name}</p>
                    <p className="text-xs text-muted-foreground">ID: {cs.service?.id}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      {combo && (
        <EditComboDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          combo={combo}
          onSubmit={(data) => updateMutation.mutate(data)}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar combo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El combo{' '}
              <span className="font-semibold text-foreground">"{combo.name}"</span> será eliminado
              permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────────────────────

function EditComboDialog({
  open,
  onOpenChange,
  combo,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  combo: Combo;
  onSubmit: (data: UpdateComboRequest) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<UpdateComboRequest>({
    name: combo.name,
    price: combo.price,
    duration: combo.duration,
    status: combo.status,
  });

  useEffect(() => {
    setFormData({
      name: combo.name,
      price: combo.price,
      duration: combo.duration,
      status: combo.status,
    });
  }, [combo]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? Number(value) : value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Combo</DialogTitle>
          <DialogDescription>Actualiza la información del combo</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="detail-edit-name">Nombre</Label>
            <Input
              id="detail-edit-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Combo Familiar"
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="detail-edit-price">Precio</Label>
              <Input
                id="detail-edit-price"
                name="price"
                type="number"
                min={0}
                value={formData.price}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detail-edit-duration">Duración (días)</Label>
              <Input
                id="detail-edit-duration"
                name="duration"
                type="number"
                min={1}
                value={formData.duration}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="detail-edit-status">Estado</Label>
            <select
              id="detail-edit-status"
              name="status"
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as EComboStatus })
              }
              disabled={isLoading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={EComboStatus.ACTIVE}>Activo</option>
              <option value={EComboStatus.INACTIVE}>Inactivo</option>
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar cambios'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
